<template>
  <Modal
    :show="show"
    container-class="atb-w-1/3"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="emit('close')"
  >
    <template #header>{{ $t('mdsAttachTitle') }}</template>
    <template #body>
      <div
        id="mdsAttachModal"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        <span class="atb-font-bold">{{ $t('mdsAttachComment') }}</span>
        <AgGridVue
          id="mdsAttachGrid"
          :style="{ height: '300px' }"
          class="ag-theme-eds atb-w-full"
          :column-defs="columnDefs"
          :row-data="rowData"
          :row-selection="'multiple'"
          :suppress-row-click-selection="true"
          :row-multi-select-with-click="true"
          :overlay-no-rows-template="noRowsTemplate"
          @grid-ready="onGridReady"
        />
      </div>
    </template>
    <template #footer>
      <Button
        id="mdsAttachCancelButton"
        motif="basic"
        @click="emit('close')"
        >{{ $t('mdsCancel') }}</Button
      >
      <Button
        id="mdsAttachSaveButton"
        :disabled="saving || rowData.length === 0"
        @click="attach"
        >{{ $t('mdsAttach') }}</Button
      >
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import { nextTick, Ref, ref, toRefs, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { GridApi, GridReadyEvent } from 'ag-grid-community';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import multiDatasourceApi, {
    refreshSelectedClient,
  } from '@/app/features/ams/tools/mds/multi-datasource-service';
  import { errorMessage } from '@/app/features/ams/tools/mds/mds-error';

  /**
   * 5.x "Attach an existing multi data source": every multi datasource in the system, ticked
   * when it is attached to the selected client. Attach links the ticked ones and detaches the
   * rest.
   */
  interface MdsAttachEvents {
    (_event: 'close'): void;
  }
  interface MdsAttachProps {
    show: boolean;
  }

  const props = defineProps<MdsAttachProps>();
  const emit = defineEmits<MdsAttachEvents>();
  const { show } = toRefs(props);
  const { t } = useI18n();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const gridApi = ref(null) as Ref<GridApi | null>;
  const rowData = ref<Array<Datasource>>([]);
  const saving = ref(false);

  const columnDefs = [
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('mdsColumnMultiDs'),
      sortable: true,
    },
  ];

  const noRowsTemplate = `<span>${t('mdsAttachNoSources')}</span>`;

  const attachedIds = (): Array<number> =>
    (clientStore.selectedClient?.dataSources || [])
      .filter((ds: Datasource) => ds.isMulti)
      .map((ds: Datasource) => ds.id);

  const selectAttached = () => {
    const attached = attachedIds();
    gridApi.value?.forEachNode((node) => {
      node.setSelected(attached.includes(node.data.id));
    });
  };

  const load = async () => {
    try {
      const {
        data: { data },
      } = await multiDatasourceApi.getAllDatasources();
      rowData.value = (data || [])
        .filter((ds) => ds.isMulti)
        .sort((a, b) => a.name.localeCompare(b.name));
      await nextTick();
      selectAttached();
    } catch (e) {
      alertStore.showNotification(
        errorMessage(e, t),
        t('mdsAttachTitle'),
        Motif.ERROR
      );
    }
  };

  watch(show, (open) => {
    if (open) {
      load();
    }
  });

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    selectAttached();
    gridApi.value.sizeColumnsToFit();
  };

  const attach = async () => {
    saving.value = true;
    const ids = (gridApi.value?.getSelectedRows() || []).map(
      (ds: Datasource) => ds.id
    );
    try {
      await multiDatasourceApi.setClientMultiDatasources(
        clientStore.selectedClient.id,
        ids
      );
      await refreshSelectedClient();
      alertStore.showNotification(
        t('mdsAttachSaved'),
        t('mdsAttachTitle'),
        Motif.SUCCESS
      );
      emit('close');
    } catch (e) {
      alertStore.showNotification(
        errorMessage(e, t),
        t('mdsAttachTitle'),
        Motif.ERROR
      );
    } finally {
      saving.value = false;
    }
  };
</script>
